import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SimpleCheckIn = () => {
    // Get location_id from URL
    const urlParams = new URLSearchParams(window.location.search);
    const locationId = urlParams.get('location_id');

    const [members, setMembers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredMembers, setFilteredMembers] = useState([]);
    const [selectedMember, setSelectedMember] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checking, setChecking] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [locationName, setLocationName] = useState('');

    // Fetch active members on mount
    useEffect(() => {
        if (!locationId) {
            setError('No location ID provided');
            setLoading(false);
            return;
        }
        fetchMembers();
        fetchLocationName();
    }, [locationId]);

    const fetchLocationName = async () => {
        try {
            const response = await fetch(`${API_URL}/api/ghl/location-details/${locationId}`);
            if (response.ok) {
                const data = await response.json();
                setLocationName(data.name || 'Studio');
            }
        } catch (e) {
            console.error('Failed to fetch location name:', e);
        }
    };

    const fetchWithRetry = async (url, options = {}, retries = 3) => {
        for (let i = 0; i < retries; i++) {
            try {
                const response = await fetch(url, options);
                if (response.status === 503) {
                    // Service unavailable - wait and retry
                    if (i < retries - 1) {
                        await new Promise(r => setTimeout(r, 1000 * (i + 1)));
                        continue;
                    }
                }
                return response;
            } catch (e) {
                if (i < retries - 1) {
                    await new Promise(r => setTimeout(r, 1000 * (i + 1)));
                    continue;
                }
                throw e;
            }
        }
    };

    const fetchMembers = async () => {
        try {
            setLoading(true);
            const response = await fetchWithRetry(`${API_URL}/api/ghl/contacts/${locationId}`);
            if (!response || !response.ok) {
                throw new Error('Failed to load members');
            }
            const data = await response.json();
            setMembers(data.contacts || []);
            setError(null);
        } catch (e) {
            setError('Failed to load members. Please refresh the page.');
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    // Filter members as user types
    useEffect(() => {
        if (searchTerm.length > 0) {
            const filtered = members.filter(m => 
                (m.contactName || '').toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredMembers(filtered.slice(0, 10)); // Show max 10 results
        } else {
            setFilteredMembers([]);
        }
    }, [searchTerm, members]);

    const handleSelectMember = (member) => {
        setSelectedMember(member);
        setSearchTerm('');
        setFilteredMembers([]);
    };

    const handleConfirmCheckIn = async () => {
        if (!selectedMember) return;

        setChecking(true);
        setError(null);

        try {
            const response = await fetch(`${API_URL}/api/simple-checkin/${locationId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    contact_id: selectedMember.id,
                    contact_name: selectedMember.contactName
                })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setSuccess({
                    name: selectedMember.contactName,
                    time: new Date().toLocaleTimeString(),
                    count: data.checkin_count
                });
                // Reset after 4 seconds
                setTimeout(() => {
                    setSuccess(null);
                    setSelectedMember(null);
                }, 4000);
            } else {
                setError(data.detail || 'Check-in failed. Please try again.');
                setSelectedMember(null);
            }
        } catch (e) {
            setError('Check-in failed. Please try again.');
            setSelectedMember(null);
        } finally {
            setChecking(false);
        }
    };

    const handleCancel = () => {
        setSelectedMember(null);
        setSearchTerm('');
    };

    // Styles
    const styles = {
        container: {
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
        },
        card: {
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            padding: '40px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        },
        title: {
            color: '#fff',
            fontSize: '2rem',
            textAlign: 'center',
            marginBottom: '10px'
        },
        subtitle: {
            color: '#aaa',
            fontSize: '1rem',
            textAlign: 'center',
            marginBottom: '30px'
        },
        input: {
            width: '100%',
            padding: '15px 20px',
            fontSize: '1.2rem',
            border: 'none',
            borderRadius: '10px',
            background: 'rgba(255, 255, 255, 0.9)',
            marginBottom: '10px',
            boxSizing: 'border-box'
        },
        memberList: {
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '10px',
            maxHeight: '300px',
            overflowY: 'auto'
        },
        memberItem: {
            padding: '15px 20px',
            borderBottom: '1px solid #eee',
            cursor: 'pointer',
            fontSize: '1.1rem',
            transition: 'background 0.2s'
        },
        confirmBox: {
            textAlign: 'center'
        },
        confirmName: {
            color: '#4ade80',
            fontSize: '1.8rem',
            marginBottom: '30px'
        },
        button: {
            padding: '15px 40px',
            fontSize: '1.2rem',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            margin: '10px',
            transition: 'transform 0.2s'
        },
        confirmBtn: {
            background: '#4ade80',
            color: '#000'
        },
        cancelBtn: {
            background: '#666',
            color: '#fff'
        },
        successBox: {
            textAlign: 'center'
        },
        successIcon: {
            fontSize: '4rem',
            marginBottom: '20px'
        },
        successName: {
            color: '#4ade80',
            fontSize: '2rem',
            marginBottom: '10px'
        },
        successMsg: {
            color: '#fff',
            fontSize: '1.2rem'
        },
        errorBox: {
            background: 'rgba(239, 68, 68, 0.2)',
            border: '1px solid #ef4444',
            borderRadius: '10px',
            padding: '15px',
            marginBottom: '20px',
            color: '#fca5a5',
            textAlign: 'center'
        },
        loadingText: {
            color: '#fff',
            textAlign: 'center',
            fontSize: '1.2rem'
        }
    };

    // Error state - no location
    if (!locationId) {
        return (
            <div style={styles.container}>
                <div style={styles.card}>
                    <h1 style={{...styles.title, color: '#ef4444'}}>Error</h1>
                    <p style={{color: '#fff', textAlign: 'center'}}>
                        No location ID provided. Please use the correct check-in URL.
                    </p>
                </div>
            </div>
        );
    }

    // Loading state
    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.card}>
                    <p style={styles.loadingText}>Loading members...</p>
                </div>
            </div>
        );
    }

    // Success state
    if (success) {
        return (
            <div style={styles.container}>
                <div style={styles.card}>
                    <div style={styles.successBox}>
                        <div style={styles.successIcon}>✓</div>
                        <div style={styles.successName}>{success.name}</div>
                        <div style={styles.successMsg}>Check-in successful!</div>
                        {success.count && (
                            <div style={{color: '#aaa', marginTop: '10px'}}>
                                Total check-ins: {success.count}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Confirmation state
    if (selectedMember) {
        return (
            <div style={styles.container}>
                <div style={styles.card}>
                    <div style={styles.confirmBox}>
                        <h2 style={{color: '#fff', marginBottom: '20px'}}>CONFIRM CHECK-IN FOR</h2>
                        <div style={styles.confirmName}>{selectedMember.contactName}</div>
                        <div>
                            <button 
                                style={{...styles.button, ...styles.confirmBtn}}
                                onClick={handleConfirmCheckIn}
                                disabled={checking}
                            >
                                {checking ? 'Checking in...' : 'CONFIRM'}
                            </button>
                            <button 
                                style={{...styles.button, ...styles.cancelBtn}}
                                onClick={handleCancel}
                                disabled={checking}
                            >
                                CANCEL
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Main search state
    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <h1 style={styles.title}>{locationName || 'Member Check-In'}</h1>
                <p style={styles.subtitle}>Enter your name to check in</p>

                {error && <div style={styles.errorBox}>{error}</div>}

                <input
                    type="text"
                    placeholder="Type your name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={styles.input}
                    autoFocus
                    data-testid="search-input"
                />

                {filteredMembers.length > 0 && (
                    <div style={styles.memberList}>
                        {filteredMembers.map((member) => (
                            <div
                                key={member.id}
                                style={styles.memberItem}
                                onClick={() => handleSelectMember(member)}
                                onMouseEnter={(e) => e.target.style.background = '#e0e7ff'}
                                onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                data-testid={`member-${member.id}`}
                            >
                                {member.contactName}
                            </div>
                        ))}
                    </div>
                )}

                {searchTerm.length > 0 && filteredMembers.length === 0 && (
                    <p style={{color: '#aaa', textAlign: 'center', marginTop: '20px'}}>
                        No members found matching "{searchTerm}"
                    </p>
                )}
            </div>
        </div>
    );
};

export default SimpleCheckIn;
